// Critical-fields contract — see aegle-docs critical_fields_contract.md
import { Box, FormControlLabel, Switch, Tooltip, Typography } from "@mui/material";

interface CriticalCoverage {
  mapped: number;
  total: number;
}

interface MappingSummaryProps {
  showUnmapped: boolean;
  onShowUnmappedChange: (show: boolean) => void;
  /**
   * Critical-field coverage for the current schema. When `total` is 0 (schema
   * has no critical fields), the coverage line is suppressed entirely.
   */
  criticalCoverage: CriticalCoverage;
}

/**
 * Compact summary line above the mapping table. Holds the at-a-glance
 * critical-coverage readout and the show-unmapped toggle. The previous
 * version layered "X mapped" + "K unmapped" + "K critical unmapped" chips
 * — collapsed to the one signal that drives a reviewer's next action.
 */
export const MappingSummary = ({ showUnmapped, onShowUnmappedChange, criticalCoverage }: MappingSummaryProps) => {
  const hasCriticalSlots = criticalCoverage.total > 0;

  return (
    <Box
      mb={2}
      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}
    >
      {hasCriticalSlots ? (
        <Tooltip
          title="Fields the system relies on for claimant matching, duplicate detection, and claims totals."
          arrow
          placement="top-start"
        >
          <Typography variant="body2" color="text.secondary" sx={{ cursor: "help" }}>
            Critical: {criticalCoverage.mapped} of {criticalCoverage.total} mapped
          </Typography>
        </Tooltip>
      ) : (
        <span />
      )}
      <FormControlLabel
        control={
          <Switch checked={showUnmapped} onChange={(e) => onShowUnmappedChange(e.target.checked)} size="small" />
        }
        label={<Typography variant="body2">Show unmapped headers</Typography>}
      />
    </Box>
  );
};
